import { GridOptions } from "./entities/gridOptions";
import { GridOptionsWrapper } from "./gridOptionsWrapper";
import { SelectionController } from "./selectionController";
import { ColumnApi } from "./columnController/columnApi";
import { ColumnController } from "./columnController/columnController";
import { RowRenderer } from "./rendering/rowRenderer";
import { HeaderRootComp } from "./headerRendering/headerRootComp";
import { FilterManager } from "./filter/filterManager";
import { ValueService } from "./valueService/valueService";
import { EventService } from "./eventService";
import { GridPanel } from "./gridPanel/gridPanel";
import { GridApi } from "./gridApi";
import { ColumnFactory } from "./columnController/columnFactory";
import { DisplayedGroupCreator } from "./columnController/displayedGroupCreator";
import { ExpressionService } from "./valueService/expressionService";
import { TemplateService } from "./templateService";
import { PopupService } from "./widgets/popupService";
import { Logger, LoggerFactory } from "./logger";
import { ColumnUtils } from "./columnController/columnUtils";
import { AutoWidthCalculator } from "./rendering/autoWidthCalculator";
import { HorizontalResizeService } from "./headerRendering/horizontalResizeService";
import { ComponentMeta, Context, ContextParams } from "./context/context";
import { GridCore } from "./gridCore";
import { StandardMenuFactory } from "./headerRendering/standardMenu";
import { DragAndDropService } from "./dragAndDrop/dragAndDropService";
import { DragService } from "./dragAndDrop/dragService";
import { SortController } from "./sortController";
import { FocusController } from "./focusController";
import { MouseEventService } from "./gridPanel/mouseEventService";
import { CellNavigationService } from "./cellNavigationService";
import { Events, GridReadyEvent } from "./events";
import { ValueFormatterService } from "./rendering/valueFormatterService";
import { DyCheckbox } from "./widgets/dyCheckbox";
import { DyRadioButton } from "./widgets/dyRadioButton";
import { VanillaFrameworkOverrides } from "./vanillaFrameworkOverrides";
import { IFrameworkOverrides } from "./interfaces/iFrameworkOverrides";
import { ScrollVisibleService } from "./gridPanel/scrollVisibleService";
import { StylingService } from "./styling/stylingService";
import { ColumnHoverService } from "./rendering/columnHoverService";
import { ColumnAnimationService } from "./rendering/columnAnimationService";
import { AutoGroupColService } from "./columnController/autoGroupColService";
import { PaginationProxy } from "./pagination/paginationProxy";
import { PaginationAutoPageSizeService } from "./pagination/paginationAutoPageSizeService";
import { IRowModel } from "./interfaces/iRowModel";
import { Constants } from "./constants/constants";
import { ValueCache } from "./valueService/valueCache";
import { ChangeDetectionService } from "./valueService/changeDetectionService";
import { AlignedGridsService } from "./alignedGridsService";
import { UserComponentFactory } from "./components/framework/userComponentFactory";
import { DyGridRegisteredComponentInput, UserComponentRegistry } from "./components/framework/userComponentRegistry";
import { DyComponentUtils } from "./components/framework/dyComponentUtils";
import { ComponentMetadataProvider } from "./components/framework/componentMetadataProvider";
import { Beans } from "./rendering/beans";
import { Environment } from "./environment";
import { AnimationFrameService } from "./misc/animationFrameService";
import { NavigationService } from "./gridPanel/navigationService";
import { MaxDivHeightScaler } from "./rendering/maxDivHeightScaler";
import { SelectableService } from "./rowNodes/selectableService";
import { AutoHeightCalculator } from "./rendering/row/autoHeightCalculator";
import { PaginationComp } from "./pagination/paginationComp";
import { ResizeObserverService } from "./misc/resizeObserverService";
import { OverlayWrapperComponent } from "./rendering/overlays/overlayWrapperComponent";
import { Module } from "./interfaces/iModule";
import { DyGroupComponent } from "./widgets/dyGroupComponent";
import { DyDialog } from "./widgets/dyDialog";
import { DyPanel } from "./widgets/dyPanel";
import { DyInputTextField } from "./widgets/dyInputTextField";
import { DyInputTextArea } from "./widgets/dyInputTextArea";
import { DySlider } from "./widgets/dySlider";
import { DyColorPicker } from "./widgets/dyColorPicker";
import { DyInputNumberField } from "./widgets/dyInputNumberField";
import { DyInputRange } from "./widgets/dyInputRange";
import { DySelect } from "./widgets/dySelect";
import { DyAngleSelect } from "./widgets/dyAngleSelect";
import { DyToggleButton } from "./widgets/dyToggleButton";
import { DetailRowCompCache } from "./rendering/row/detailRowCompCache";
import { RowPositionUtils } from "./entities/rowPosition";
import { CellPositionUtils } from "./entities/cellPosition";
import { PinnedRowModel } from "./pinnedRowModel/pinnedRowModel";
import { IComponent } from "./interfaces/iComponent";
import { ModuleRegistry } from "./modules/moduleRegistry";
import { ModuleNames } from "./modules/moduleNames";
import { UndoRedoService } from "./undoRedo/undoRedoService";
import { Component } from "./widgets/component";
import { DyStackComponentsRegistry } from "./components/dyStackComponentsRegistry";
import { HeaderPositionUtils } from "./headerRendering/header/headerPosition";
import { HeaderNavigationService } from "./headerRendering/header/headerNavigationService";
import { missing, exists } from "./utils/generic";
import { assign, iterateObject } from "./utils/object";
import { ColumnDefFactory } from "./columnController/columnDefFactory";
import { RowCssClassCalculator } from "./rendering/row/rowCssClassCalculator";
import { RowNodeBlockLoader } from "./rowNodeCache/rowNodeBlockLoader";
import { RowNodeSorter } from "./rowNodes/rowNodeSorter";

export interface GridParams {
  // used by Web Components
  globalEventListener?: Function;

  // these are used by ng1 only
  $scope?: any;
  $compile?: any;
  quickFilterOnScope?: any;

  // this allows the base frameworks (React, NG2, etc) to provide alternative cellRenderers and cellEditors
  frameworkOverrides?: IFrameworkOverrides;

  // bean instances to add to the context
  providedBeanInstances?: { [key: string]: any; };

  modules?: Module[];

  // Alternative UI root class. Default is GridCore.
  rootComponent?: { new(): Component; };
}

export class Grid {

  private context: Context;
  protected logger: Logger;
  private readonly gridOptions: GridOptions;

  constructor(eGridDiv: HTMLElement, gridOptions: GridOptions, params?: GridParams) {
    if (!eGridDiv) {
      console.error('DYNA Grid: no div element provided to the grid');
      return;
    }

    if (!gridOptions) {
      console.error('DYNA Grid: no gridOptions provided to the grid');
      return;
    }

    const debug = !!gridOptions.debug;

    this.gridOptions = gridOptions;

    const registeredModules = this.getRegisteredModules(params);

    const beanClasses = this.createBeansList(registeredModules);
    const providedBeanInstances = this.createProvidedBeans(eGridDiv, params);

    if (!beanClasses) { return; } // happens when no row model found

    const contextParams: ContextParams = {
      providedBeanInstances: providedBeanInstances,
      beanClasses: beanClasses,
      debug: debug
    };

    this.logger = new Logger('DYNA Grid', () => gridOptions.debug);
    const contextLogger = new Logger('Context', () => contextParams.debug);
    this.context = new Context(contextParams, contextLogger);

    this.registerModuleUserComponents(registeredModules);

    this.registerStackComponents(registeredModules);

    const gridCoreClass = (params && params.rootComponent) || GridCore;
    const gridCore = new gridCoreClass();
    this.context.createBean(gridCore);

    this.setColumnsAndData();
    this.dispatchGridReadyEvent(gridOptions);
    const isEnterprise = ModuleRegistry.isRegistered(ModuleNames.ProCoreModule);
    this.logger.log(`initialised successfully, enterprise = ${isEnterprise}`);
  }

  private registerStackComponents(registeredModules: Module[]): void {
    const dyStackComponents = this.createDyStackComponentsList(registeredModules);
    const dyStackComponentsRegistry =
      this.context.getBean('dyStackComponentsRegistry') as DyStackComponentsRegistry;
    dyStackComponentsRegistry.setupComponents(dyStackComponents);
  }

  private getRegisteredModules(params?: GridParams): Module[] {
    const passedViaConstructor: Module[] | undefined | null = params ? params.modules : null;
    const registered = ModuleRegistry.getRegisteredModules();

    const allModules: Module[] = [];
    const mapNames: { [name: string]: boolean; } = {};

    // adds to list and removes duplicates
    function addModule(moduleBased: boolean, mod: Module) {
      function addIndividualModule(currentModule: Module) {
        if (!mapNames[currentModule.moduleName]) {
          mapNames[currentModule.moduleName] = true;
          allModules.push(currentModule);
          ModuleRegistry.register(currentModule, moduleBased);
        }
      }

      addIndividualModule(mod);
      if (mod.dependantModules) {
        mod.dependantModules.forEach(addModule.bind(null, moduleBased));
      }
    }

    if (passedViaConstructor) {
      passedViaConstructor.forEach(addModule.bind(null, true));
    }

    if (registered) {
      registered.forEach(addModule.bind(null, !ModuleRegistry.isPackageBased()));
    }

    return allModules;
  }

  private registerModuleUserComponents(registeredModules: Module[]): void {
    const userComponentRegistry: UserComponentRegistry = this.context.getBean('userComponentRegistry');

    const moduleUserComps: { componentName: string, componentClass: DyGridRegisteredComponentInput<IComponent<any>>; }[]
      = this.extractModuleEntity(registeredModules,
        (module) => module.userComponents ? module.userComponents : []);

    moduleUserComps.forEach(compMeta => {
      userComponentRegistry.registerDefaultComponent(compMeta.componentName, compMeta.componentClass);
    });
  }

  private createProvidedBeans(eGridDiv: HTMLElement, params?: GridParams): any {
    let frameworkOverrides = params ? params.frameworkOverrides : null;
    if (missing(frameworkOverrides)) {
      frameworkOverrides = new VanillaFrameworkOverrides();
    }

    const seed = {
      gridOptions: this.gridOptions,
      eGridDiv: eGridDiv,
      $scope: params ? params.$scope : null,
      $compile: params ? params.$compile : null,
      quickFilterOnScope: params ? params.quickFilterOnScope : null,
      globalEventListener: params ? params.globalEventListener : null,
      frameworkOverrides: frameworkOverrides
    };
    if (params && params.providedBeanInstances) {
      assign(seed, params.providedBeanInstances);
    }

    return seed;
  }

  private createDyStackComponentsList(registeredModules: Module[]): any[] {
    let components: ComponentMeta[] = [
      { componentName: 'DyCheckbox', componentClass: DyCheckbox },
      { componentName: 'DyRadioButton', componentClass: DyRadioButton },
      { componentName: 'DyToggleButton', componentClass: DyToggleButton },
      { componentName: 'DyInputTextField', componentClass: DyInputTextField },
      { componentName: 'DyInputTextArea', componentClass: DyInputTextArea },
      { componentName: 'DyInputNumberField', componentClass: DyInputNumberField },
      { componentName: 'DyInputRange', componentClass: DyInputRange },
      { componentName: 'DySelect', componentClass: DySelect },
      { componentName: 'DySlider', componentClass: DySlider },
      { componentName: 'DyAngleSelect', componentClass: DyAngleSelect },
      { componentName: 'DyColorPicker', componentClass: DyColorPicker },
      { componentName: 'DyGridComp', componentClass: GridPanel },
      { componentName: 'DyHeaderRoot', componentClass: HeaderRootComp },
      { componentName: 'DyPagination', componentClass: PaginationComp },
      { componentName: 'DyOverlayWrapper', componentClass: OverlayWrapperComponent },
      { componentName: 'DyGroupComponent', componentClass: DyGroupComponent },
      { componentName: 'DyPanel', componentClass: DyPanel },
      { componentName: 'DyDialog', componentClass: DyDialog }
    ];

    const moduleDyStackComps = this.extractModuleEntity(registeredModules,
      (module) => module.dyStackComponents ? module.dyStackComponents : []);

    components = components.concat(moduleDyStackComps);

    return components;
  }

  private createBeansList(registeredModules: Module[]): any[] | undefined {
    const rowModelClass = this.getRowModelClass(registeredModules);

    if (!rowModelClass) { return; }

    // beans should only contain SERVICES, it should NEVER contain COMPONENTS

    const beans = [
      rowModelClass, Beans, RowPositionUtils, CellPositionUtils, HeaderPositionUtils,
      PaginationAutoPageSizeService, GridApi, UserComponentRegistry, DyComponentUtils,
      ComponentMetadataProvider, ResizeObserverService, UserComponentFactory,
      MaxDivHeightScaler, AutoHeightCalculator, HorizontalResizeService,
      PinnedRowModel, DragService, DisplayedGroupCreator, EventService, GridOptionsWrapper,
      PopupService, SelectionController, FilterManager, ColumnController, HeaderNavigationService,
      PaginationProxy, RowRenderer, ExpressionService, ColumnFactory, TemplateService,
      AlignedGridsService, NavigationService, ValueCache, ValueService, LoggerFactory,
      ColumnUtils, AutoWidthCalculator, StandardMenuFactory, DragAndDropService, ColumnApi,
      FocusController, MouseEventService, Environment, CellNavigationService, ValueFormatterService,
      StylingService, ScrollVisibleService, SortController, ColumnHoverService, ColumnAnimationService,
      SelectableService, AutoGroupColService, ChangeDetectionService, AnimationFrameService,
      DetailRowCompCache, UndoRedoService, DyStackComponentsRegistry, ColumnDefFactory,
      RowCssClassCalculator, RowNodeBlockLoader, RowNodeSorter
    ];

    const moduleBeans = this.extractModuleEntity(registeredModules, (module) => module.beans ? module.beans : []);
    beans.push(...moduleBeans);

    // check for duplicates, as different modules could include the same beans that
    // they depend on, eg ClientSideRowModel in enterprise, and ClientSideRowModel in community
    const beansNoDuplicates: any[] = [];
    beans.forEach(bean => {
      if (beansNoDuplicates.indexOf(bean) < 0) {
        beansNoDuplicates.push(bean);
      }
    });

    return beansNoDuplicates;
  }

  private extractModuleEntity(moduleEntities: any[], extractor: (module: any) => any) {
    return [].concat(...moduleEntities.map(extractor));
  }

  private setColumnsAndData(): void {
    const gridOptionsWrapper: GridOptionsWrapper = this.context.getBean('gridOptionsWrapper');
    const columnController: ColumnController = this.context.getBean('columnController');
    const columnDefs = gridOptionsWrapper.getColumnDefs();

    columnController.setColumnDefs(columnDefs || [], "gridInitializing");

    const rowModel: IRowModel = this.context.getBean('rowModel');
    rowModel.start();
  }

  private dispatchGridReadyEvent(gridOptions: GridOptions): void {
    const eventService: EventService = this.context.getBean('eventService');
    const readyEvent: GridReadyEvent = {
      type: Events.EVENT_GRID_READY,
      api: gridOptions.api!,
      columnApi: gridOptions.columnApi!
    };
    eventService.dispatchEvent(readyEvent);
  }

  private getRowModelClass(registeredModules: Module[]): any {
    let rowModelType = this.gridOptions.rowModelType;

    // default to client side
    if (!rowModelType) {
      rowModelType = Constants.ROW_MODEL_TYPE_CLIENT_SIDE;
    }

    const rowModelClasses: { [name: string]: { new(): IRowModel; }; } = {};
    registeredModules.forEach(module => {
      iterateObject(module.rowModels, (key: string, value: { new(): IRowModel; }) => {
        rowModelClasses[key] = value;
      });
    });

    const rowModelClass = rowModelClasses[rowModelType];

    if (exists(rowModelClass)) { return rowModelClass; }

    if (rowModelType === Constants.ROW_MODEL_TYPE_INFINITE) {
      console.error(`DYNA Grid: Row Model "Infinite" not found. Please ensure the ${ModuleNames.InfiniteRow} is registered.';`);
    }

    console.error('DYNA Grid: could not find matching row model for rowModelType ' + rowModelType);
    if (rowModelType === Constants.ROW_MODEL_TYPE_VIEWPORT) {
      console.error(`DYNA Grid: Row Model "Viewport" not found. Please ensure the DYNA Grid Enterprise Module ${ModuleNames.ViewportRow} is registered.';`);
    }

    if (rowModelType === Constants.ROW_MODEL_TYPE_SERVER_SIDE) {
      console.error(`DYNA Grid: Row Model "Server Side" not found. Please ensure the DYNA Grid Enterprise Module ${ModuleNames.ServerSideRow} is registered.';`);
    }

    if (rowModelType === Constants.ROW_MODEL_TYPE_CLIENT_SIDE) {
      console.error(`DYNA Grid: Row Model "Client Side" not found. Please ensure the ${ModuleNames.ClientSideRow} is registered.';`);
    }
  }

  public destroy(): void {
    this.gridOptions.api!.destroy();
  }
}
